// src/utils/api.js
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
// Token management helper
const tokenManager = {
  getAccessToken: () => localStorage.getItem("accessToken"),
  getRefreshToken: () => localStorage.getItem("refreshToken"),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem("accessToken", accessToken);
    if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  },
  clearTokens: () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  },
};

// Token refresh function
async function refreshAccessToken() {
  const refreshToken = tokenManager.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const res = await fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });

    if (!res.ok) {
      tokenManager.clearTokens();
      return null;
    }

    const data = await res.json();
    if (data.success && data.data?.accessToken) {
      tokenManager.setTokens(data.data.accessToken);
      return data.data.accessToken;
    }

    return null;
  } catch (err) {
    tokenManager.clearTokens();
    return null;
  }
}

// Base request helper with automatic token refresh
async function request(method, url, data = null, useAuth = false) {
  const headers = { "Content-Type": "application/json" };
  
  // Add token if auth is required
  if (useAuth) {
    const token = tokenManager.getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const options = {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  };

  try {
    let res = await fetch(API_URL + url, options);
    
    // If 401 and we have a refresh token, try to refresh
    if (res.status === 401 && useAuth && tokenManager.getRefreshToken()) {
      const newToken = await refreshAccessToken();
      
      if (newToken) {
        // Retry request with new token
        headers.Authorization = `Bearer ${newToken}`;
        res = await fetch(API_URL + url, { ...options, headers });
      } else {
        // Refresh failed, redirect to login
        tokenManager.clearTokens();
        window.location.href = "/login";
        return {
          success: false,
          message: "Session expired. Please login again.",
          status: 401,
        };
      }
    }

    // Parse response
    let output;
    try {
      output = await res.json();
    } catch {
      output = null;
    }

    if (!res.ok) {
      return {
        success: false,
        message: output?.message || output?.error || "Request failed",
        errors: output?.errors || null,
        status: res.status,
      };
    }

    return output || { success: true, data: null };
  } catch (err) {
    return {
      success: false,
      message: "Network error — backend not responding",
    };
  }
}

export const api = {
  // ==================== AUTH ====================
  register: async (data) => {
    const res = await request("POST", "/auth/register", data);
    if (res.success && res.data) {
      tokenManager.setTokens(res.data.accessToken, res.data.refreshToken);
    }
    return res;
  },

  login: async (data) => {
    const res = await request("POST", "/auth/login", data);
    if (res.success && res.data) {
      tokenManager.setTokens(res.data.accessToken, res.data.refreshToken);
    }
    return res;
  },

  logout: async () => {
    const res = await request("POST", "/auth/logout", null, true);
    tokenManager.clearTokens();
    return res;
  },

  getMe: () => request("GET", "/auth/me", null, true),

  refreshToken: refreshAccessToken,

  // Token utilities
  isAuthenticated: () => !!tokenManager.getAccessToken(),
  clearAuth: () => tokenManager.clearTokens(),

  // ==================== PRODUCTS ====================
  getProducts: async () => {
    // Fetch all products at once (they'll be paginated in the UI)
    const res = await request("GET", "/products?limit=100");
    return res?.data || [];
  },

  getProduct: async (slugOrId) => {
    const res = await request("GET", `/products/${slugOrId}`);
    return res?.data || null;
  },

  getCategories: async () => {
    const res = await request("GET", "/products/categories/all");
    return res?.data || [];
  },

  getFeatured: async () => {
    const res = await request("GET", "/products/featured/all");
    return res?.data || [];
  },

  // ==================== ADMIN PRODUCT ====================
  createProduct: (data) => request("POST", "/products", data, true),
  updateProduct: (id, data) => request("PUT", `/products/${id}`, data, true),
  deleteProduct: (id) => request("DELETE", `/products/${id}`, null, true),

  // ==================== CART ====================
  getCart: () => request("GET", "/cart", null, true),
  
  addToCart: (data) => {
    // Validate data before sending
    if (!data.productId) {
      return Promise.resolve({
        success: false,
        message: "Product ID is required",
      });
    }
    return request("POST", "/cart/add", data, true);
  },
  
  updateCart: (data) => {
    // Validate data
    if (!data.productId || !data.quantity) {
      return Promise.resolve({
        success: false,
        message: "Product ID and quantity are required",
      });
    }
    return request("PUT", "/cart/update", data, true);
  },
  
  removeFromCart: (productId) => {
    if (!productId) {
      return Promise.resolve({
        success: false,
        message: "Product ID is required",
      });
    }
    return request("DELETE", `/cart/remove/${productId}`, null, true);
  },
  
  clearCart: () => request("DELETE", "/cart/clear", null, true),
  
  syncCart: (guestCartItems) => request("POST", "/cart/sync", { guestCartItems }, true),

  // ==================== ORDERS ====================
  createOrder: (data) => {
    // Validate required fields
    if (!data.items || data.items.length === 0) {
      return Promise.resolve({
        success: false,
        message: "No items in order",
      });
    }
    if (!data.shippingAddress) {
      return Promise.resolve({
        success: false,
        message: "Shipping address is required",
      });
    }
    return request("POST", "/orders", data, true);
  },
  
  getMyOrders: () => request("GET", "/orders", null, true),
  getOrder: (id) => request("GET", `/orders/${id}`, null, true),
  
  // NEW: Get detailed order information
  getOrderDetails: (orderId) => request("GET", `/orders/${orderId}`, null, true),
  
  updatePayment: (id, data) => request("PUT", `/orders/${id}/payment`, data, true),
  cancelOrder: (id) => request("PUT", `/orders/${id}/cancel`, null, true),

  // ==================== ADMIN ORDERS ====================
  getAllOrders: () => request("GET", "/orders/admin/all", null, true),
  updateOrderStatus: (id, data) => request("PUT", `/orders/${id}/status`, data, true),

  // ==================== USER ====================
  getProfile: () => request("GET", "/users/profile", null, true),
  updateProfile: (data) => request("PUT", "/users/profile", data, true),
  addAddress: (data) => request("POST", "/users/address", data, true),
  updateAddress: (id, data) => request("PUT", `/users/address/${id}`, data, true),
  deleteAddress: (id) => request("DELETE", `/users/address/${id}`, null, true),

  // Super Admin only
  getAllUsers: () => request("GET", "/users/admin/all", null, true),
  updateUserRole: (userId, data) => request("PUT", `/users/${userId}/role`, data, true),

  // ==================== PAYMENT ====================
  initiateUPIPayment: (data) => request("POST", "/payment/upi/initiate", data, true),
  verifyUPIPayment: (data) => request("POST", "/payment/upi/verify", data, true),
  initiatePayPalPayment: (data) => request("POST", "/payment/paypal/initiate", data, true),
  verifyPayPalPayment: (data) => request("POST", "/payment/paypal/verify", data, true),
  getPaymentStatus: (paymentId) => request("GET", `/payment/status/${paymentId}`, null, true),

  // ==================== CONTACT ====================
  sendContactMessage: (data) => request("POST", "/contact", data, false), // Public endpoint
  getAllContactMessages: () => request("GET", "/contact/admin/all", null, true), // Admin only
  markContactMessageAsRead: (messageId) => request("PUT", `/contact/${messageId}/read`, null, true), // Admin only
  deleteContactMessage: (messageId) => request("DELETE", `/contact/${messageId}`, null, true), // Admin only
};